import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import ConvertBoxInitial from './initial/initial.index';
import { __ConvertBoxRef, __TypeOfOrderEnum } from './initial/initial.interface';
import { __ConvertBoxDataType, __ConvertBoxProps, __ConvertBoxStatesEnum } from './convertBox.interface';
import ConvertBoxBuySelectCard from './buySelectCard/buySelectCard.index';
import BuyWithdrawMethod from './buyWithdrawMethod/buyWithdrawMethod.index';
import BuyPaymentMethod from './buyPaymentMethod/buyPaymentMethod.index';
import SellWithdrawMethod from './sellWithdrawMethod/sellWithdrawMethod.index';
import SellDeposit from './sellDeposit/sellDeposit.index';
import { __BuyPaymentMethodsEnum } from './buyPaymentMethod/buyPaymentMethod.interface';
import SellCryptoTxId from './sellCryptoTxId/sellCryptoTxId.index';
import EnterPayeerAccount from './enterPayeerAccount/enterPayeerAccount.index';
import ConvertBoxConfirmOrderModal, {
    ConvertBoxConfirmOrderDetailModalRef,
} from '30pay/trade/convertBox/confirmOrderModal/orderDetailModal.index';
import { post } from 'scripts';
import { API } from 'data/30pay.api';
import { toast } from 'react-toastify';
import { Wallet } from 'interfaces';
import { getWallet } from '30pay/wallet/wallet.service';

export interface ConvertBoxDataType extends __ConvertBoxDataType {}
export { __ConvertBoxStatesEnum as ConvertBoxStatesEnum };

/**
 * For now we treat all BUY flows as "wallet-only" so we don't show BuyPaymentMethod.
 * If later you re-enable gateway or add other methods, tighten this condition.
 */
function isWalletOnlyBuy(data?: ConvertBoxDataType) {
    return data?.typeOfOrder === __TypeOfOrderEnum.buy;
}

const ConvertBox: React.ForwardRefRenderFunction<__ConvertBoxRef, __ConvertBoxProps> = (
    props: __ConvertBoxProps,
    ref,
) => {
    const initialConvertBox = useRef<__ConvertBoxRef>(null);
    const [state, setState] = useState<__ConvertBoxStatesEnum>(__ConvertBoxStatesEnum.Initial);
    const [data, setData] = useState<ConvertBoxDataType>();
    const [history, setHistory] = useState<__ConvertBoxStatesEnum[]>([__ConvertBoxStatesEnum.Initial]);
    // For UUSD confirmation modal
    const [uusdConfirmData, setUusdConfirmData] = useState<ConvertBoxDataType | undefined>();
    const [paymentUrl, setPaymentUrl] = useState<string | undefined>();
    const [wallet, setWallet] = useState<Wallet | undefined>();
    const [walletLoading, setWalletLoading] = useState<boolean>(false);

    useEffect(() => {
        getWallet(setWalletLoading, setWallet);
    }, []);

    function isUusdBuy(data?: ConvertBoxDataType) {
        return data?.typeOfOrder === __TypeOfOrderEnum.buy && data.destCoin?.symbol?.toUpperCase() === 'UUSD';
    }

    const confirmOrderModalRef = useRef<ConvertBoxConfirmOrderDetailModalRef>(null);

    useImperativeHandle(ref, () => ({
        setCoin: (coin) => initialConvertBox.current?.setCoin(coin),
    }));

    function resetToInitial() {
        setData(undefined);
        setState(__ConvertBoxStatesEnum.Initial);
        setHistory([__ConvertBoxStatesEnum.Initial]);
    }

    function onFinishHandler() {
        props.onFinish?.();
        resetToInitial();
    }

    function determineNextState(): __ConvertBoxStatesEnum | undefined {
        if (!data) return;

        const typeOfOrder = data.typeOfOrder;
        const destSymbol = data.destCoin?.symbol?.toUpperCase();

        // BUY FLOW
        if (typeOfOrder === __TypeOfOrderEnum.buy) {
            switch (state) {
                case __ConvertBoxStatesEnum.Initial: {
                    // If we're in wallet-only mode, skip BuyPaymentMethod completely.
                    if (isWalletOnlyBuy(data)) {
                        if (destSymbol === 'PAYEER') {
                            return __ConvertBoxStatesEnum.EnterPayeerAccount;
                        }
                        return __ConvertBoxStatesEnum.BuyWithdrawMethod;
                    }

                    // Normal flow when you actually want to show BuyPaymentMethod.
                    return __ConvertBoxStatesEnum.BuyPaymentMethod;
                }

                case __ConvertBoxStatesEnum.BuyPaymentMethod: {
                    switch (data.buyPaymentMethod) {
                        case __BuyPaymentMethodsEnum.Gateway:
                            return __ConvertBoxStatesEnum.BuySelectCard;

                        case __BuyPaymentMethodsEnum.Wallet:
                            if (destSymbol === 'PAYEER') {
                                return __ConvertBoxStatesEnum.EnterPayeerAccount;
                            }
                            return __ConvertBoxStatesEnum.BuyWithdrawMethod;

                        default:
                            // Original logic: "UUSD with Wallet" or any other special case
                            onFinishHandler();
                            return __ConvertBoxStatesEnum.Initial;
                    }
                }

                case __ConvertBoxStatesEnum.BuySelectCard:
                    if (destSymbol === 'PAYEER') {
                        return __ConvertBoxStatesEnum.EnterPayeerAccount;
                    }
                    return __ConvertBoxStatesEnum.BuyWithdrawMethod;

                case __ConvertBoxStatesEnum.BuyWithdrawMethod:
                    onFinishHandler();
                    return __ConvertBoxStatesEnum.Initial;
            }
        }

        // SELL FLOW
        if (typeOfOrder === __TypeOfOrderEnum.sell) {
            switch (state) {
                case __ConvertBoxStatesEnum.Initial:
                    return __ConvertBoxStatesEnum.SellWithdrawMethod;
                case __ConvertBoxStatesEnum.SellWithdrawMethod:
                    return __ConvertBoxStatesEnum.SellDeposit;
                case __ConvertBoxStatesEnum.SellDeposit:
                    return __ConvertBoxStatesEnum.SellCryptoTxId;
                case __ConvertBoxStatesEnum.SellCryptoTxId:
                    onFinishHandler();
                    return __ConvertBoxStatesEnum.Initial;
            }
        }
    }

    function onBackClickHandler() {
        setHistory((prev) => {
            if (prev.length <= 1) return prev; // already at root

            const newHistory = prev.slice(0, -1);
            const prevState = newHistory[newHistory.length - 1];

            setState(prevState);
            props.onChangeState?.(prevState);

            return newHistory;
        });
    }

    function onSubmitHandler(e: ConvertBoxDataType) {
        setData((prevData) => {
            const merged: ConvertBoxDataType = { ...prevData, ...e };

            // In wallet-only mode, we never show BuyPaymentMethod, so force the method to Wallet.
            if (isWalletOnlyBuy(merged)) {
                merged.buyPaymentMethod = __BuyPaymentMethodsEnum.Wallet;
            }

            return merged;
        });
    }

    function handleUusdBuySubmit() {
        if (!uusdConfirmData) return;

        // start loading state on the confirm modal
        confirmOrderModalRef.current?.startLoading();

        const UUSDBody = {
            // keep the same mapping as in the old submitHandler
            source: uusdConfirmData.buyPaymentMethod === __BuyPaymentMethodsEnum.Gateway ? 1 : 0,
            platform: 1,
            amount: uusdConfirmData.srcValue,
            cardNumberId: uusdConfirmData.bankAccountId,
        };

        post<any>(API.orderTransaction.buyUUSD, UUSDBody)
            .then((res) => {
                const value = res?.value?.value;

                if (value?.paymentUrl) {
                    // save it so the button label can change
                    setPaymentUrl(value.paymentUrl);
                    toast.info('در حال انتقال به درگاه پرداخت...', { autoClose: 2000 });
                    setTimeout(() => window.location.replace(value.paymentUrl), 2000);
                } else {
                    confirmOrderModalRef.current?.showResultContent(true, value?.trackingCode, {
                        state: value?.state,
                    });
                    confirmOrderModalRef.current?.stopLoading();
                }
            })
            .catch(() => {
                confirmOrderModalRef.current?.showResultContent(false);
                confirmOrderModalRef.current?.stopLoading();
            });
    }

    useEffect(() => {
        if (data === undefined) return;

        // 🔥 Special case: UUSD buy → show confirm modal right here,
        // instead of going to any withdraw method step.
        if (state === __ConvertBoxStatesEnum.Initial && isUusdBuy(data)) {
            setUusdConfirmData(data); // open modal via state
            return; // ⬅️ do NOT run determineNextState for this case
        }

        const nextState = determineNextState();
        if (nextState !== undefined && nextState !== state) {
            setState(nextState);
            setHistory((prev) => [...prev, nextState]);
            props.onChangeState?.(nextState);
        }
        // We intentionally only react to `data` changes to keep the wizard transition logic simple.
    }, [data]);

    return (
        <>
            {state === __ConvertBoxStatesEnum.Initial && (
                <ConvertBoxInitial
                    isPreview={props.isPreview}
                    title={props.title}
                    onSubmit={onSubmitHandler}
                    wallet={wallet}
                    ref={initialConvertBox}
                />
            )}

            {state === __ConvertBoxStatesEnum.BuySelectCard && (
                <ConvertBoxBuySelectCard onSubmit={onSubmitHandler} onBack={onBackClickHandler} />
            )}

            {state === __ConvertBoxStatesEnum.BuyWithdrawMethod && (
                <BuyWithdrawMethod onFinish={onFinishHandler} onBack={onBackClickHandler} data={data} />
            )}

            {state === __ConvertBoxStatesEnum.BuyPaymentMethod && !isWalletOnlyBuy(data) && (
                <BuyPaymentMethod onSubmit={onSubmitHandler} onBack={onBackClickHandler} data={data} />
            )}

            {state === __ConvertBoxStatesEnum.EnterPayeerAccount && (
                <EnterPayeerAccount onFinish={onFinishHandler} onBack={onBackClickHandler} data={data} />
            )}

            {state === __ConvertBoxStatesEnum.SellWithdrawMethod && (
                <SellWithdrawMethod onSubmit={onSubmitHandler} onBack={onBackClickHandler} data={data} />
            )}

            {state === __ConvertBoxStatesEnum.SellDeposit && (
                <SellDeposit
                    onFinish={onFinishHandler}
                    onSubmit={onSubmitHandler}
                    onBack={onBackClickHandler}
                    data={data}
                />
            )}

            {state === __ConvertBoxStatesEnum.SellCryptoTxId && (
                <SellCryptoTxId onFinish={onFinishHandler} onBack={onBackClickHandler} data={data} />
            )}

            {uusdConfirmData && (
                <ConvertBoxConfirmOrderModal
                    data={uusdConfirmData}
                    ref={confirmOrderModalRef}
                    isOpen={!!uusdConfirmData}
                    onClose={(needReFetch) => {
                        setUusdConfirmData(undefined);
                        // After closing the modal, finish the flow and reset.
                        // If you want to stay on Initial always:
                        onFinishHandler();
                        // If you need to re-fetch something when success:
                        // if (needReFetch) { ... }
                    }}
                    submitButton={{
                        title: paymentUrl ? 'انتقال به درگاه' : 'تایید سفارش',
                        onClick: () => {
                            if (paymentUrl) {
                                // if we already have a paymentUrl, just go there
                                window.location.replace(paymentUrl);
                            } else {
                                // first click: actually create the UUSD order
                                handleUusdBuySubmit();
                            }
                        },
                    }}
                />
            )}
        </>
    );
};

export default React.forwardRef(ConvertBox);
